import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";

// Uses one clean image; the "before" layer is the same image with a grime filter,
// guaranteeing identical framing for a convincing before/after. No people.
export const BeforeAfterSlider = ({ image, label, testid, className = "" }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    setPos(pct);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  const start = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      onMouseDown={start}
      onTouchStart={start}
      className={`relative w-full select-none overflow-hidden rounded-xl bg-[#0A2342] cursor-ew-resize ${className}`}
    >
      {/* AFTER (clean) base */}
      <img src={image} alt="Clean solar panels — after" className="block h-full w-full object-cover pointer-events-none" draggable={false} />

      {/* BEFORE (dirty) overlay, clipped from left */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img
          src={image}
          alt="Dirty solar panels — before"
          draggable={false}
          className="block h-full w-full object-cover"
          style={{ width: containerRef.current ? containerRef.current.offsetWidth : "100%", maxWidth: "none", filter: "sepia(0.5) saturate(0.65) brightness(0.68) contrast(1.05)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(110,86,44,0.28), rgba(74,58,30,0.4))", mixBlendMode: "multiply" }} />
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(60,45,25,0.55) 1px, transparent 1.6px)", backgroundSize: "7px 7px" }} />
      </div>

      {/* Category label */}
      {label && (
        <span className="absolute left-3 top-3 z-20 rounded bg-[#1E5EDB] px-2.5 py-1 font-display text-[11px] font-bold tracking-wide text-white">
          {label}
        </span>
      )}

      {/* BEFORE / AFTER tags */}
      <span className="absolute bottom-3 left-3 z-20 rounded bg-black/55 px-2 py-0.5 font-display text-[10.5px] font-bold tracking-widest text-white">BEFORE</span>
      <span className="absolute bottom-3 right-3 z-20 rounded bg-black/55 px-2 py-0.5 font-display text-[10.5px] font-bold tracking-widest text-white">AFTER</span>

      {/* Divider + handle */}
      <div className="absolute top-0 bottom-0 z-10" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-[3px] bg-white/90" />
        <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.3)]">
          <ChevronsLeftRight size={18} className="text-[#1E5EDB]" />
        </div>
      </div>
    </div>
  );
};
